import React, { useState, useEffect } from 'react';
import {
  X,
  Calendar,
  Loader2,
  CheckCircle,
  AlertTriangle,
  Info,
  RefreshCw,
} from 'lucide-react';
import { usePaymentSchedules } from '../../hooks/usePaymentSchedules';
import { assignPaymentScheduleToStudents, replacePaymentSchedule } from '@/services/tuitionService';
import { getAcademicYears } from '@/services/academicManagementService';

interface AssignPaymentScheduleModalProps {
  studentId: string;
  studentName: string;
  hasExistingSchedule?: boolean;
  existingScheduleName?: string;
  onClose: () => void;
  onSuccess: () => void;
}

const scheduleTypeLabels: Record<string, string> = {
  ANNUAL: 'Annuel',
  SEMESTRIAL: 'Semestriel',
  TRIMESTRIAL: 'Trimestriel',
  MONTHLY: 'Mensuel',
  CUSTOM: 'Personnalisé',
};

export const AssignPaymentScheduleModal: React.FC<AssignPaymentScheduleModalProps> = ({
  studentId,
  studentName,
  hasExistingSchedule = false,
  existingScheduleName,
  onClose,
  onSuccess,
}) => {
  const [selectedTemplateId, setSelectedTemplateId] = useState<string>('');
  const [academicYearId, setAcademicYearId] = useState<string>('');
  const [academicYears, setAcademicYears] = useState<Array<{ value: string; label: string; isCurrent?: boolean }>>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Confirmation dialog state for replacing schedule
  const [showReplaceConfirm, setShowReplaceConfirm] = useState(false);

  // Fetch payment schedule templates
  const { data: schedulesData, isLoading: loadingSchedules } = usePaymentSchedules({ includeArchived: false });
  const schedules = schedulesData?.data || [];

  // Load academic years
  useEffect(() => {
    const loadAcademicYears = async () => {
      try {
        const years = await getAcademicYears();
        setAcademicYears(years);
        // Set current academic year by default
        const currentYear = years.find((y) => y.isCurrent);
        if (currentYear) {
          setAcademicYearId(currentYear.value);
        } else if (years.length > 0) {
          setAcademicYearId(years[0].value);
        }
      } catch (err) {
        console.error('Error loading academic years:', err);
      }
    };
    loadAcademicYears();
  }, []);

  const handleSubmit = async () => {
    if (!selectedTemplateId || !academicYearId) {
      setError('Veuillez sélectionner un échéancier et une année académique');
      return;
    }

    // If student already has a schedule, show confirmation dialog
    if (hasExistingSchedule) {
      setShowReplaceConfirm(true);
      return;
    }

    await performAssignment();
  };

  const performAssignment = async () => {
    setIsSubmitting(true);
    setError(null);

    try {
      const result = await assignPaymentScheduleToStudents({
        templateId: selectedTemplateId,
        studentIds: [studentId],
        academicYearId,
      });

      if (result.assignedCount > 0) {
        setSuccess('Échéancier assigné avec succès');
        setTimeout(() => {
          onSuccess();
          onClose();
        }, 1500);
      } else if (result.skippedCount > 0) {
        setError('Cet étudiant a déjà un échéancier assigné pour cette année académique');
      } else if (result.errors && result.errors.length > 0) {
        setError(result.errors[0]);
      }
    } catch (err: any) {
      setError(err.message || "Erreur lors de l'assignation de l'échéancier");
    } finally {
      setIsSubmitting(false);
    }
  };

  const performReplacement = async () => {
    setShowReplaceConfirm(false);
    setIsSubmitting(true);
    setError(null);

    try {
      await replacePaymentSchedule({
        studentId,
        templateId: selectedTemplateId,
        academicYearId,
      });

      setSuccess('Échéancier remplacé avec succès');
      setTimeout(() => {
        onSuccess();
        onClose();
      }, 1500);
    } catch (err: any) {
      setError(err.message || "Erreur lors du remplacement de l'échéancier");
    } finally {
      setIsSubmitting(false);
    }
  };

  const selectedSchedule = schedules.find((s) => s.id === selectedTemplateId);

  return (
    <>
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-xl shadow-xl max-w-xl w-full max-h-[90vh] overflow-hidden flex flex-col">
          {/* Header */}
          <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
            <div>
              <h2 className="text-xl font-bold text-gray-900">
                {hasExistingSchedule ? 'Modifier l\'Échéancier' : 'Assigner un Échéancier'}
              </h2>
              <p className="text-sm text-gray-500 mt-1">{studentName}</p>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <X className="w-5 h-5 text-gray-500" />
            </button>
          </div>

          {/* Content */}
          <div className="p-6 space-y-6 overflow-y-auto flex-1">
            {/* Info about existing schedule */}
            {hasExistingSchedule && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <RefreshCw className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-amber-900">
                      Cet étudiant a déjà un échéancier assigné
                    </p>
                    <p className="text-sm text-amber-700 mt-1">
                      Échéancier actuel: <strong>{existingScheduleName || 'Non spécifié'}</strong>
                    </p>
                    <p className="text-xs text-amber-600 mt-2">
                      Sélectionnez un nouvel échéancier pour remplacer l'existant.
                      Une confirmation vous sera demandée avant le changement.
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Info banner */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <Info className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm text-blue-800">
                    L'échéancier définit les dates et montants des échéances de paiement pour l'étudiant.
                    Les montants seront calculés en fonction du solde à payer de l'étudiant.
                  </p>
                </div>
              </div>
            </div>

            {/* Academic Year Selection */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Année Académique <span className="text-red-500">*</span>
              </label>
              <select
                value={academicYearId}
                onChange={(e) => setAcademicYearId(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              >
                <option value="">Sélectionner une année</option>
                {academicYears.map((year) => (
                  <option key={year.value} value={year.value}>
                    {year.label} {year.isCurrent && '(Année en cours)'}
                  </option>
                ))}
              </select>
            </div>

            {/* Schedule Selection */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Type d'Échéancier <span className="text-red-500">*</span>
              </label>
              {loadingSchedules ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="w-6 h-6 animate-spin text-primary-600" />
                </div>
              ) : schedules.length === 0 ? (
                <div className="text-center py-8 bg-gray-50 rounded-lg">
                  <Calendar className="w-10 h-10 text-gray-400 mx-auto mb-2" />
                  <p className="text-sm text-gray-500">Aucun échéancier disponible</p>
                  <p className="text-xs text-gray-400 mt-1">
                    Créez d'abord un échéancier dans la page Frais & Relances
                  </p>
                </div>
              ) : (
                <div className="space-y-2 max-h-64 overflow-y-auto">
                  {schedules.map((schedule) => (
                    <div
                      key={schedule.id}
                      onClick={() => setSelectedTemplateId(schedule.id)}
                      className={`p-4 rounded-lg border-2 cursor-pointer transition-all ${
                        selectedTemplateId === schedule.id
                          ? 'border-primary-500 bg-primary-50'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div
                            className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                              selectedTemplateId === schedule.id
                                ? 'bg-primary-500 text-white'
                                : 'bg-gray-100 text-gray-500'
                            }`}
                          >
                            <Calendar className="w-5 h-5" />
                          </div>
                          <div>
                            <div className="flex items-center gap-2">
                              <span className="font-medium text-gray-900">
                                {schedule.name}
                              </span>
                              {schedule.isDefault && (
                                <span className="px-2 py-0.5 text-xs bg-blue-100 text-blue-700 rounded-full">
                                  Par défaut
                                </span>
                              )}
                            </div>
                            <div className="flex items-center gap-2 mt-1">
                              <span className="text-xs text-gray-500">
                                {scheduleTypeLabels[schedule.type] || schedule.type}
                              </span>
                              <span className="text-xs text-gray-400">•</span>
                              <span className="text-xs text-gray-500">
                                {schedule.installments?.length || 0} échéance(s)
                              </span>
                            </div>
                          </div>
                        </div>
                        {selectedTemplateId === schedule.id && (
                          <CheckCircle className="w-5 h-5 text-primary-600" />
                        )}
                      </div>
                      {/* Show installments preview when selected */}
                      {selectedTemplateId === schedule.id && schedule.installments && schedule.installments.length > 0 && (
                        <div className="mt-3 pt-3 border-t border-gray-200">
                          <p className="text-xs font-medium text-gray-600 mb-2">Aperçu des échéances:</p>
                          <div className="space-y-1">
                            {schedule.installments.slice(0, 4).map((inst, idx) => (
                              <div key={idx} className="flex justify-between text-xs">
                                <span className="text-gray-600">{inst.label}</span>
                                <span className="text-gray-900 font-medium">
                                  {inst.percentageOfTotal}%
                                </span>
                              </div>
                            ))}
                            {schedule.installments.length > 4 && (
                              <p className="text-xs text-gray-400 italic">
                                +{schedule.installments.length - 4} autre(s)...
                              </p>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Error Message */}
            {error && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <AlertTriangle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-red-800">{error}</p>
                </div>
              </div>
            )}

            {/* Success Message */}
            {success && (
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-green-800">{success}</p>
                </div>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="px-6 py-4 border-t border-gray-200 flex gap-3">
            <button
              onClick={onClose}
              disabled={isSubmitting}
              className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 disabled:opacity-50"
            >
              Annuler
            </button>
            <button
              onClick={handleSubmit}
              disabled={isSubmitting || !selectedTemplateId || !academicYearId || !!success}
              className={`flex-1 px-4 py-2 text-white rounded-lg disabled:opacity-50 flex items-center justify-center gap-2 ${
                hasExistingSchedule
                  ? 'bg-amber-600 hover:bg-amber-700'
                  : 'bg-primary-600 hover:bg-primary-700'
              }`}
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  {hasExistingSchedule ? 'Remplacement...' : 'Assignation...'}
                </>
              ) : (
                <>
                  {hasExistingSchedule ? (
                    <>
                      <RefreshCw className="w-4 h-4" />
                      Remplacer
                    </>
                  ) : (
                    <>
                      <Calendar className="w-4 h-4" />
                      Assigner
                    </>
                  )}
                </>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Confirmation Dialog for Replacement */}
      {showReplaceConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-bold text-gray-900">Confirmer le remplacement</h3>
            </div>
            <div className="p-6">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-amber-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <AlertTriangle className="w-6 h-6 text-amber-600" />
                </div>
                <div>
                  <p className="text-gray-700">
                    Êtes-vous sûr de vouloir remplacer l'échéancier actuel de cet étudiant ?
                  </p>
                  <div className="mt-3 p-3 bg-gray-50 rounded-lg">
                    <div className="flex justify-between text-sm mb-2">
                      <span className="text-gray-500">Échéancier actuel:</span>
                      <span className="font-medium text-gray-900">{existingScheduleName || 'Non spécifié'}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-500">Nouvel échéancier:</span>
                      <span className="font-medium text-primary-600">{selectedSchedule?.name || '-'}</span>
                    </div>
                  </div>
                  <p className="text-sm text-amber-600 mt-3">
                    Cette action supprimera l'ancien échéancier et toutes ses échéances.
                    Les paiements déjà effectués ne seront pas affectés.
                  </p>
                </div>
              </div>
            </div>
            <div className="px-6 py-4 border-t border-gray-200 flex gap-3">
              <button
                onClick={() => setShowReplaceConfirm(false)}
                className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
              >
                Annuler
              </button>
              <button
                onClick={performReplacement}
                className="flex-1 px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 flex items-center justify-center gap-2"
              >
                <RefreshCw className="w-4 h-4" />
                Confirmer le remplacement
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default AssignPaymentScheduleModal;
